import { CreateMessageTemplateDto, MessageTemplateDto, UpdateMessageTemplateDto } from '@shared/core/message-templates'
import ms from 'ms'
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import { useHandleRetry } from 'src/modules/core/hooks/use-handle-retry'
import { GET_PROFILE_CACHE_KEY } from 'src/modules/user/hooks/use-profile'
import { MessageTemplatesService } from 'src/services/message-templates.service'

// Cache keys
export const GET_MESSAGE_TEMPLATES_CACHE_KEY = 'get-message-templates'
export const GET_MESSAGE_TEMPLATE_BY_ID_CACHE_KEY = 'get-message-template-by-id'
export const CREATE_MESSAGE_TEMPLATE_CACHE_KEY = 'create-message-template'
export const UPDATE_MESSAGE_TEMPLATE_CACHE_KEY = 'update-message-template'
export const DELETE_MESSAGE_TEMPLATE_CACHE_KEY = 'delete-message-template'
export const SET_AS_DEFAULT_CACHE_KEY = 'set-as-default'

// Get all message templates
export function useMessageTemplates(options?: UseQueryOptions<MessageTemplateDto[]>) {
  const handleRetry = useHandleRetry()
  return useQuery([GET_MESSAGE_TEMPLATES_CACHE_KEY], () => MessageTemplatesService.getMessageTemplates(), {
    staleTime: ms('5 minutes'),
    retry: handleRetry,
    ...options,
  })
}

// Get message template by ID
export function useMessageTemplateById(id: string, options?: UseQueryOptions<MessageTemplateDto>) {
  const handleRetry = useHandleRetry()
  return useQuery(
    [GET_MESSAGE_TEMPLATE_BY_ID_CACHE_KEY, id],
    () => MessageTemplatesService.getMessageTemplateById(id),
    {
      staleTime: ms('5 minutes'),
      retry: handleRetry,
      ...options,
    },
  )
}

// Create message template
export function useCreateMessageTemplate() {
  const queryClient = useQueryClient()
  return useMutation(
    CREATE_MESSAGE_TEMPLATE_CACHE_KEY,
    (dto: CreateMessageTemplateDto) => MessageTemplatesService.createMessageTemplate(dto),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_MESSAGE_TEMPLATES_CACHE_KEY])
        queryClient.invalidateQueries([GET_PROFILE_CACHE_KEY])
      },
    },
  )
}

// Update message template
export function useUpdateMessageTemplate() {
  const queryClient = useQueryClient()
  return useMutation(
    UPDATE_MESSAGE_TEMPLATE_CACHE_KEY,
    ({ id, dto }: { id: string; dto: UpdateMessageTemplateDto }) =>
      MessageTemplatesService.updateMessageTemplate(id, dto),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries([GET_MESSAGE_TEMPLATES_CACHE_KEY])
        queryClient.invalidateQueries([GET_MESSAGE_TEMPLATE_BY_ID_CACHE_KEY, id])
        queryClient.invalidateQueries([GET_PROFILE_CACHE_KEY])
      },
    },
  )
}

// Delete message template
export function useDeleteMessageTemplate() {
  const queryClient = useQueryClient()
  return useMutation(
    DELETE_MESSAGE_TEMPLATE_CACHE_KEY,
    (id: string) => MessageTemplatesService.deleteMessageTemplate(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_MESSAGE_TEMPLATES_CACHE_KEY])
      },
    },
  )
}

// Set as default
export function useSetAsDefault() {
  const queryClient = useQueryClient()
  return useMutation(SET_AS_DEFAULT_CACHE_KEY, (id: string) => MessageTemplatesService.setAsDefault(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_MESSAGE_TEMPLATES_CACHE_KEY])
      queryClient.invalidateQueries([GET_PROFILE_CACHE_KEY])
    },
  })
}
